import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

const useStyles = makeStyles({
  root: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "16px",
    fontWeight: "normal",
    cursor: "inherit",
    userSelect: "none",
    border: "1px solid transparent",
    lineHeight: "1",
  },
  success: {
    backgroundColor: "#E2F8E0",
    color: "#257223",
    borderColor: "#D0F4CD",
  },
  danger: {
    backgroundColor: "#FBE5E5",
    color: "#993434",
    borderColor: "#F8D3D3",
  },
  warning: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    borderColor: "#ffeeba",
  },
  info: {
    backgroundColor: "#d1ecf1",
    color: "#0c5460",
    borderColor: "#bee5eb",
  },
  default: {
    backgroundColor: "#e2e3e5",
    color: "#383d41",
    borderColor: "#d6d8db",
  },
  light: {
    backgroundColor: "#F8F8F8",
    color: "#0C0E29",
    borderColor: "#EDEDED",
  },
  simulation: {
    background: "var(--color-bg-simulation)",
    color: "var(--color-text-simulation)",
    borderColor: "var(--color-border-simulation)",
  },
  extraSmall: {
    height: "1.125rem",
    fontSize: "0.75rem",
    padding: ".125rem .25rem",
    borderRadius: "4px",
  },
  small: {
    height: "1.25rem",
    fontSize: ".875rem",
    padding: "0rem .25rem",
    borderRadius: "4px",
  },
  medium: {
    height: "1.5rem", // 24px
    fontSize: "1rem", // 16px
    padding: "0rem 0.5rem", // 8px
    borderRadius: "8px",
  },
  large: {
    height: "2rem",
    fontSize: "1.25rem",
    padding: "0rem 1rem",
  },
  adornment: {
    display: "inline-flex",
    alignItems: "center",
  },
  extraSmallAdornment: {
    "& svg": {
      width: 12,
      height: 12,
    },
  },
  smallAdornment: {
    "& svg": {
      width: 12,
      height: 12,
      margin: "0 .125rem",
    },
  },
  mediumAdornment: {
    "& svg": {
      width: 16,
      height: 16,
      margin: "0 .25rem",
    },
  },
  largeAdornment: {
    "& svg": {
      width: 20,
      height: 20,
      margin: "0 .5rem",
    },
  },

  // padding for adornment combinations
  extraSmallStartAdornment: {
    paddingLeft: "0.25rem",
  },
  extraSmallEndAdornment: {
    paddingRight: "0.25rem",
  },
  extraSmallBothAdornments: {
    paddingLeft: "0.25rem",
    paddingRight: "0.25rem",
  },

  smallStartAdornment: {
    paddingLeft: "0.5rem",
  },
  smallEndAdornment: {
    paddingRight: "0.5rem",
  },
  smallBothAdornments: {
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
  },

  mediumStartAdornment: {
    paddingLeft: ".0625rem",
  },
  mediumEndAdornment: {
    paddingRight: "0.0625rem",
  },
  mediumBothAdornments: {
    paddingLeft: "0.0625rem",
    paddingRight: "0.0625rem",
  },

  largeStartAdornment: {
    paddingLeft: "1rem",
  },
  largeEndAdornment: {
    paddingRight: "1rem",
  },
  largeBothAdornments: {
    paddingLeft: "1rem",
    paddingRight: "1rem",
  },
});

const Chip = ({ children, variant = "default", size = "medium", startAdornment, endAdornment, onClick, className, ...props }) => {
  const classes = useStyles();

  const adornmentPadClass = clsx({
    [classes[`${size}StartAdornment`]]: startAdornment && !endAdornment,
    [classes[`${size}EndAdornment`]]: endAdornment && !startAdornment,
    [classes[`${size}BothAdornments`]]: startAdornment && endAdornment,
  });

  return (
    <div className={clsx(classes.root, classes[variant], classes[size], adornmentPadClass, className)} onClick={onClick} {...props}>
      {startAdornment && <span className={`${classes.adornment} ${classes[`${size}Adornment`]}`}>{startAdornment}</span>}
      {children}
      {endAdornment && <span className={`${classes.adornment} ${classes[`${size}Adornment`]}`}>{endAdornment}</span>}
    </div>
  );
};

Chip.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["success", "danger", "warning", "info", "default", "simulation"]),
  size: PropTypes.oneOf(["extraSmall", "small", "medium", "large"]),
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default Chip;
